import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  UserPlus, Search, UserCheck, UserX, IdCard, FileWarning, AlertCircle,
  DoorOpen, RotateCcw, Eye, Upload, CheckCircle2, XCircle, FileSpreadsheet,
} from "lucide-react";

const LEAVE_REASONS = ["İstifa", "İşten çıkarma", "Emeklilik", "Diğer"];
const ROLE_TYPES = ["Şoför", "Usta", "Ofis", "Saha Sorumlusu", "Diğer", "Belirtilmedi"];

function docBadge(warn) {
  if (warn === "expired") return <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded-full"><AlertCircle className="w-3 h-3" /> DOLDU</span>;
  if (warn === "soon") return <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full"><FileWarning className="w-3 h-3" /> 30gün</span>;
  return null;
}

function roleBadge(role) {
  const map = {
    "Şoför": "bg-blue-100 text-blue-800",
    "Usta": "bg-slate-100 text-slate-700",
    "Ofis": "bg-purple-100 text-purple-700",
    "Saha Sorumlusu": "bg-emerald-100 text-emerald-700",
    "Diğer": "bg-amber-100 text-amber-700",
    "Belirtilmedi": "bg-slate-100 text-slate-400",
  };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${map[role] || "bg-slate-100 text-slate-500"}`}>{role || "—"}</span>;
}

export default function Drivers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("active");
  const [roleFilter, setRoleFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [termOpen, setTermOpen] = useState(null);
  const [rehireOpen, setRehireOpen] = useState(null);
  const [warnCount, setWarnCount] = useState(0);
  const [form, setForm] = useState({
    name: "", tc: "", phone: "", hire_date: "", role_type: "Şoför",
    license_expiry: "", src_expiry: "", psycho_expiry: "",
  });
  const [termForm, setTermForm] = useState({ end_date: "", leave_reason: "", note: "" });
  const [rehireForm, setRehireForm] = useState({ start_date: "", note: "" });

  // Excel import
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const importFileRef = useRef(null);

  const load = async () => {
    setBusy(true);
    try {
      const params = { filter };
      if (roleFilter !== "all") params.role = roleFilter;
      const [drv, warn] = await Promise.all([
        api.get("/drivers", { params }),
        api.get("/drivers/document-warnings"),
      ]);
      setRows(drv.data || []);
      setWarnCount(warn.data?.count || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Personel listesi yüklenemedi");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, roleFilter]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(s) || (r.tc || "").includes(s));
  }, [rows, q]);

  const openAdd = () => {
    setForm({ name: "", tc: "", phone: "", hire_date: "", role_type: "Şoför",
      license_expiry: "", src_expiry: "", psycho_expiry: "" });
    setAddOpen(true);
  };

  const saveNew = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) return toast.error("Ad Soyad zorunlu");
    if (!/^\d{11}$/.test(form.tc)) return toast.error("TC 11 haneli olmalı");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.hire_date)) return toast.error("İşe giriş tarihi zorunlu");
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      payload.name = form.name.trim();
      payload.tc = form.tc.trim();
      payload.hire_date = form.hire_date;
      await api.post("/drivers", payload);
      toast.success("Personel eklendi");
      setAddOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kayıt başarısız");
    }
  };

  const submitTerminate = async () => {
    if (!termOpen) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(termForm.end_date)) return toast.error("Çıkış tarihi zorunlu");
    if (!termForm.leave_reason) return toast.error("Ayrılma sebebi zorunlu");
    try {
      await api.post(`/drivers/${termOpen.id}/terminate`, termForm);
      toast.success(`${termOpen.name} işten çıkarıldı`);
      setTermOpen(null);
      setTermForm({ end_date: "", leave_reason: "", note: "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İşlem başarısız");
    }
  };

  const submitRehire = async () => {
    if (!rehireOpen) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(rehireForm.start_date)) return toast.error("Giriş tarihi zorunlu");
    try {
      await api.post(`/drivers/${rehireOpen.id}/rehire`, rehireForm);
      toast.success(`${rehireOpen.name} tekrar işe alındı`);
      setRehireOpen(null);
      setRehireForm({ start_date: "", note: "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İşlem başarısız");
    }
  };

  const uploadExcel = async (file) => {
    if (!file) return;
    setImportBusy(true);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/drivers/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Attach role selection state to each row
      data.rows = data.rows.map((r) => ({ ...r, chosen_role: r.role_type || (r.status === "matched" ? r.existing_role : "") }));
      setImportPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Excel okunamadı");
    } finally {
      setImportBusy(false);
      if (importFileRef.current) importFileRef.current.value = "";
    }
  };

  const commitImport = async () => {
    if (!importPreview) return;
    // Validate: all non-error rows must have chosen_role
    const missing = importPreview.rows.filter((r) => r.status !== "error" && !r.chosen_role);
    if (missing.length > 0) {
      return toast.error(`${missing.length} satır için görev seçilmedi`);
    }
    const rows = importPreview.rows
      .filter((r) => r.status !== "error" && !r.conflict)
      .map((r) => ({
        tc: r.tc,
        name: r.name,
        hire_date: r.hire_date,
        end_date: r.end_date,
        role_type: r.chosen_role,
      }));
    try {
      await api.post("/drivers/import-commit", { rows });
      toast.success(`İçe aktarıldı: ${rows.length} kayıt`);
      setImportOpen(false);
      setImportPreview(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kayıt başarısız");
    }
  };

  const totalActive = rows.filter((r) => r.status === "Aktif").length;
  const totalLeft = rows.filter((r) => r.status === "Ayrıldı").length;

  return (
    <div className="space-y-6" data-testid="drivers-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Kadro</div>
          <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">Personel</h1>
          <p className="mt-1 text-sm text-slate-500">Şoför, usta, ofis ve saha görevlilerini yönet. Kalıcı silme yok — geçmiş korunur.</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel'den Yükle
            </Button>
          )}
          <Button onClick={openAdd} className="bg-[#1E3A8A] hover:bg-[#1e40af]" data-testid="add-driver-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Personel Ekle
          </Button>
        </div>
      </div>

      {warnCount > 0 && (
        <Card className="border-amber-300 bg-amber-50/60" data-testid="doc-warnings-card">
          <CardContent className="p-4 flex items-center gap-3">
            <FileWarning className="w-6 h-6 text-amber-700 shrink-0" />
            <div className="text-sm text-amber-900">
              <b>{warnCount}</b> şoförün belgesi 30 gün içinde doluyor veya süresi doldu.
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="inline-flex bg-slate-100 rounded-md p-1">
          {[
            ["active", "Aktif", totalActive],
            ["left", "Ayrılanlar", totalLeft],
            ["all", "Tümü", rows.length],
          ].map(([k, lbl]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 text-sm rounded ${filter === k ? "bg-white shadow font-semibold text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
              data-testid={`filter-${k}`}
            >
              {lbl}
            </button>
          ))}
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px] h-10" data-testid="role-filter"><SelectValue placeholder="Görev" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Görevler</SelectItem>
            {ROLE_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Ad veya TC ara..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-10" data-testid="drivers-search" />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ad Soyad</TableHead>
                <TableHead>Görev</TableHead>
                <TableHead>TC</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>Belgeler</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-slate-400">
                    {busy ? "Yükleniyor..." : "Kayıt bulunamadı"}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`driver-row-${r.id}`}>
                  <TableCell>
                    <button className="text-blue-800 font-semibold hover:underline" onClick={() => navigate(`/soforler/${r.id}`)} data-testid={`driver-name-${r.id}`}>
                      {r.name}
                    </button>
                  </TableCell>
                  <TableCell>{roleBadge(r.role_type)}</TableCell>
                  <TableCell className="font-mono text-xs">{r.tc ? `${r.tc.slice(0,3)}****${r.tc.slice(-4)}` : "-"}</TableCell>
                  <TableCell>
                    {r.status === "Aktif" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
                        <UserCheck className="w-3 h-3" /> Aktif
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-medium">
                        <UserX className="w-3 h-3" /> Ayrıldı
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{docBadge(r.document_warning)}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/soforler/${r.id}`)} title="Profil" data-testid={`view-${r.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {r.status === "Aktif" ? (
                        <Button size="sm" variant="ghost" onClick={() => setTermOpen(r)} title="İşten Çıkar" data-testid={`terminate-${r.id}`}>
                          <DoorOpen className="w-4 h-4 text-amber-700" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => setRehireOpen(r)} title="Tekrar İşe Al" data-testid={`rehire-${r.id}`}>
                          <RotateCcw className="w-4 h-4 text-emerald-700" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add driver */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-driver-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <IdCard className="w-5 h-5 text-blue-700" /> Yeni Personel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Ad Soyad *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-10" data-testid="new-name" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>TC Kimlik No *</Label>
                <Input value={form.tc} onChange={(e) => setForm({ ...form, tc: e.target.value.replace(/\D/g, "").slice(0, 11) })} maxLength={11} className="mt-1 h-10 font-mono" data-testid="new-tc" />
              </div>
              <div>
                <Label>Görev *</Label>
                <Select value={form.role_type} onValueChange={(v) => setForm({ ...form, role_type: v })}>
                  <SelectTrigger className="mt-1 h-10" data-testid="new-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Telefon</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-10 font-mono" data-testid="new-phone" />
              </div>
              <div>
                <Label>İşe Giriş Tarihi *</Label>
                <Input type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} className="mt-1 h-10 font-mono" data-testid="new-hire-date" />
              </div>
            </div>
            {form.role_type === "Şoför" && (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Ehliyet Bitiş</Label>
                  <Input type="date" value={form.license_expiry} onChange={(e) => setForm({ ...form, license_expiry: e.target.value })} className="mt-1 h-9 font-mono text-sm" data-testid="new-license" />
                </div>
                <div>
                  <Label className="text-xs">SRC Bitiş</Label>
                  <Input type="date" value={form.src_expiry} onChange={(e) => setForm({ ...form, src_expiry: e.target.value })} className="mt-1 h-9 font-mono text-sm" data-testid="new-src" />
                </div>
                <div>
                  <Label className="text-xs">Psikoteknik Bitiş</Label>
                  <Input type="date" value={form.psycho_expiry} onChange={(e) => setForm({ ...form, psycho_expiry: e.target.value })} className="mt-1 h-9 font-mono text-sm" data-testid="new-psycho" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Vazgeç</Button>
            <Button onClick={saveNew} className="bg-[#1E3A8A] hover:bg-[#1e40af]" data-testid="save-new-driver">Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terminate */}
      <Dialog open={!!termOpen} onOpenChange={(v) => !v && setTermOpen(null)}>
        <DialogContent className="max-w-md" data-testid="terminate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-amber-800">
              <DoorOpen className="w-5 h-5" /> İşten Çıkar: {termOpen?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Çıkış Tarihi *</Label>
              <Input type="date" value={termForm.end_date} onChange={(e) => setTermForm({ ...termForm, end_date: e.target.value })} className="mt-1 h-10 font-mono" data-testid="term-date" />
            </div>
            <div>
              <Label>Ayrılma Sebebi *</Label>
              <Select value={termForm.leave_reason} onValueChange={(v) => setTermForm({ ...termForm, leave_reason: v })}>
                <SelectTrigger className="mt-1 h-10" data-testid="term-reason"><SelectValue placeholder="Sebep seçin" /></SelectTrigger>
                <SelectContent>
                  {LEAVE_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Not</Label>
              <Textarea rows={3} value={termForm.note} onChange={(e) => setTermForm({ ...termForm, note: e.target.value })} data-testid="term-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermOpen(null)}>Vazgeç</Button>
            <Button onClick={submitTerminate} className="bg-amber-700 hover:bg-amber-800 text-white" data-testid="term-submit">İşten Çıkar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rehire */}
      <Dialog open={!!rehireOpen} onOpenChange={(v) => !v && setRehireOpen(null)}>
        <DialogContent className="max-w-md" data-testid="rehire-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-emerald-800">
              <RotateCcw className="w-5 h-5" /> Tekrar İşe Al: {rehireOpen?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Yeni Giriş Tarihi *</Label>
              <Input type="date" value={rehireForm.start_date} onChange={(e) => setRehireForm({ ...rehireForm, start_date: e.target.value })} className="mt-1 h-10 font-mono" data-testid="rehire-date" />
            </div>
            <div>
              <Label>Not</Label>
              <Textarea rows={3} value={rehireForm.note} onChange={(e) => setRehireForm({ ...rehireForm, note: e.target.value })} data-testid="rehire-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRehireOpen(null)}>Vazgeç</Button>
            <Button onClick={submitRehire} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="rehire-submit">Tekrar İşe Al</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excel import */}
      <Dialog open={importOpen} onOpenChange={(v) => { if (!v) { setImportOpen(false); setImportPreview(null); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <FileSpreadsheet className="w-5 h-5 text-emerald-700" /> Excel'den Toplu Yükleme
            </DialogTitle>
          </DialogHeader>
          {!importPreview ? (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                Beklenen sütunlar: <b>Adı Soyadı</b>, <b>TC Kimlik No</b>, <b>Giriş Tarihi</b>, <b>Çıkış Tarihi</b> (GG.AA.YYYY). Fazladan sütunlar (sıra no vb.) yok sayılır. Dosya sunucuda saklanmaz.
              </div>
              <input ref={importFileRef} type="file" accept=".xlsx,.xlsm" onChange={(e) => uploadExcel(e.target.files?.[0])} data-testid="import-file-input" disabled={importBusy} />
              <Button variant="outline" onClick={() => importFileRef.current?.click()} disabled={importBusy} data-testid="import-file-btn">
                <Upload className="w-4 h-4 mr-2" /> {importBusy ? "Okunuyor..." : "Excel Seç ve Önizle"}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-4 text-sm bg-slate-50 rounded p-3">
                <span>Toplam: <b>{importPreview.summary.total}</b></span>
                <span className="text-emerald-700">Eşleşen: <b>{importPreview.summary.matched}</b></span>
                <span className="text-blue-700">Yeni: <b>{importPreview.summary.new}</b></span>
                <span className="text-red-700">Hatalı: <b>{importPreview.summary.errors}</b></span>
                {importPreview.only_in_system?.length > 0 && (
                  <span className="text-slate-500 ml-auto">Excel'de olmayan: <b>{importPreview.only_in_system.length}</b> (değiştirilmiyor)</span>
                )}
              </div>
              <div className="border rounded max-h-[400px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-2">Satır</th>
                      <th className="text-left px-2 py-2">Durum</th>
                      <th className="text-left px-2 py-2">Ad Soyad</th>
                      <th className="text-left px-2 py-2">TC Son 4</th>
                      <th className="text-left px-2 py-2">Giriş</th>
                      <th className="text-left px-2 py-2">Çıkış</th>
                      <th className="text-left px-2 py-2">Görev</th>
                      <th className="text-left px-2 py-2">Not</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.rows.map((r, idx) => {
                      const isError = r.status === "error";
                      const isConflict = !!r.conflict;
                      return (
                        <tr key={idx} className={isError ? "bg-red-50" : isConflict ? "bg-amber-50" : ""} data-testid={`import-row-${idx}`}>
                          <td className="px-2 py-1 font-mono">{r.row}</td>
                          <td className="px-2 py-1">
                            {isError ? <span className="text-red-700 font-bold flex items-center gap-1"><XCircle className="w-3 h-3" />Hatalı</span>
                              : r.status === "matched" ? <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Eşleşti</span>
                              : <span className="text-blue-700 flex items-center gap-1"><UserPlus className="w-3 h-3" />Yeni</span>}
                          </td>
                          <td className="px-2 py-1">{r.name}{r.existing_name && r.existing_name !== r.name && <span className="text-slate-400 ml-1">(kadroda: {r.existing_name})</span>}</td>
                          <td className="px-2 py-1 font-mono">{r.tc_last4}</td>
                          <td className="px-2 py-1 font-mono">{r.hire_date || "—"}</td>
                          <td className="px-2 py-1 font-mono">{r.end_date || "—"}</td>
                          <td className="px-2 py-1">
                            {isError ? "—" : (
                              <Select value={r.chosen_role || ""} onValueChange={(v) => {
                                const rows = [...importPreview.rows]; rows[idx] = { ...r, chosen_role: v }; setImportPreview({ ...importPreview, rows });
                              }}>
                                <SelectTrigger className="h-7 text-xs" data-testid={`import-role-${idx}`}><SelectValue placeholder="Seçin" /></SelectTrigger>
                                <SelectContent>
                                  {ROLE_TYPES.map((rt) => <SelectItem key={rt} value={rt}>{rt}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                          <td className="px-2 py-1 text-slate-600">
                            {isError && r.errors?.length > 0 && <span className="text-red-700">{r.errors.join("; ")}</span>}
                            {isConflict && <span className="text-amber-700">{r.conflict}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {importPreview.only_in_system?.length > 0 && (
                <details className="text-xs text-slate-500">
                  <summary className="cursor-pointer">Excel'de olmayan sistem kayıtları ({importPreview.only_in_system.length}) — değiştirilmiyor</summary>
                  <div className="mt-2 space-y-1 max-h-[100px] overflow-y-auto">
                    {importPreview.only_in_system.map((x) => (
                      <div key={x.id}>{x.name} ({x.tc_last4})</div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportOpen(false); setImportPreview(null); }}>Vazgeç</Button>
            {importPreview && (
              <Button onClick={commitImport} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="import-commit-btn"
                disabled={importPreview.summary.total - importPreview.summary.errors === 0}>
                Onayla ve Uygula ({importPreview.summary.matched + importPreview.summary.new} satır)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

